/**
 * Cohort Analytics UI — S6.2
 *
 *   /yonetici/kohort
 *
 * Firmanın tüm sınavlarını zaman çizgisinde ekip ortalaması ile gösterir,
 * birden fazla sınava giren adayların gelişimini sıralar, topic trend'lerini
 * ilk-son delta olarak listeler.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface TimelinePoint {
  examId: string;
  accessCode: string;
  createdAt: string;
  attemptCount: number;
  teamAverage: number;
  topicAverages: Record<string, number>;
}

interface CandidateProgress {
  candidateId: string;
  displayName: string;
  firstScore: number;
  lastScore: number;
  totalDelta: number;
  attempts: Array<{
    examId: string;
    examAccessCode: string;
    completedAt: string;
    score: number;
    scoreLevel: string | null;
  }>;
}

interface CohortData {
  timeline: TimelinePoint[];
  candidateProgress: CandidateProgress[];
  topicTrend: Record<string, { firstAvg: number; lastAvg: number; delta: number }>;
  examCount: number;
  candidatesWithMultipleExams: number;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel',
  DT: 'Teori',
};

export function CohortPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [data, setData] = useState<CohortData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<CohortData>('/api/manager/cohort');
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Kohort verisi yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, sessionPending, setLocation]);

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <Alert variant="danger">{error}</Alert>
        </main>
      </div>
    );
  }

  if (data.timeline.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <button
            type="button"
            onClick={() => setLocation('/yonetici/dashboard')}
            className="text-sm font-medium text-blue-900 hover:underline"
          >
            ← Panel
          </button>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">
            Kohort Analitiği
          </h1>
          <Card className="mt-6">
            <p className="text-center text-slate-500">
              Henüz tamamlanmış sınav yok. En az 1 sınav + 1 aday tamamlandıktan
              sonra burada zaman çizgisi görünecek.
            </p>
            <div className="mt-4 text-center">
              <Link
                href="/yonetici/sinavlar/yeni"
                className="font-medium text-blue-900 hover:underline"
              >
                İlk sınavı oluştur →
              </Link>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  // Timeline y-axis scaling
  const allTeamAvgs = data.timeline.map((t) => t.teamAverage);
  const minAvg = Math.max(0, Math.min(...allTeamAvgs) - 10);
  const maxAvg = Math.min(100, Math.max(...allTeamAvgs) + 10);
  const range = Math.max(1, maxAvg - minAvg);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation('/yonetici/dashboard')}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Panel
        </button>
        <h1 className="mt-2 text-3xl font-bold text-slate-900">
          Kohort Analitiği
        </h1>
        <p className="mt-1 text-slate-600">
          Spec 8.5 — Ekibinizin sınavlar arası gelişimini, aday bazlı ilerlemeyi
          ve konu trend'lerini izleyin.
        </p>

        {/* Summary */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Metric label="Tamamlanan Sınav" value={String(data.examCount)} />
          <Metric
            label="Çoklu Sınav Adayı"
            value={String(data.candidatesWithMultipleExams)}
          />
          <Metric
            label="İlk → Son Sınav"
            value={
              data.timeline.length >= 2
                ? `${data.timeline[0]!.teamAverage} → ${data.timeline[data.timeline.length - 1]!.teamAverage}`
                : '—'
            }
          />
        </div>

        {/* Timeline chart */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Sınav Zaman Çizgisi
          </h2>
          <p className="text-xs text-slate-500">
            Her nokta bir sınavın ekip ortalamasını gösterir
          </p>
          <div className="mt-4 flex items-end gap-2">
            {data.timeline.map((t) => {
              const heightPct = ((t.teamAverage - minAvg) / range) * 100;
              return (
                <Link
                  key={t.examId}
                  href={`/yonetici/sinavlar/${t.examId}`}
                  className="group flex flex-1 flex-col items-center"
                >
                  <span className="text-xs font-medium text-slate-700">
                    {t.teamAverage}
                  </span>
                  <div className="relative mt-1 flex h-40 w-full items-end">
                    <div
                      className="w-full rounded-t bg-blue-700 transition-all group-hover:bg-blue-800"
                      style={{ height: `${Math.max(4, heightPct)}%` }}
                      title={`${t.accessCode}: ${t.teamAverage}/100`}
                    />
                  </div>
                  <span className="mt-2 max-w-full truncate font-mono text-xs text-slate-600">
                    {t.accessCode}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {new Date(t.createdAt).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {t.attemptCount} aday
                  </span>
                </Link>
              );
            })}
          </div>
        </Card>

        {/* Topic trend */}
        {Object.keys(data.topicTrend).length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Konu Trend (İlk vs Son Sınav)
            </h2>
            <div className="mt-4 space-y-2">
              {Object.entries(data.topicTrend)
                .sort(([, a], [, b]) => b.delta - a.delta)
                .map(([topic, t]) => {
                  const arrow =
                    t.delta > 0 ? '↑' : t.delta < 0 ? '↓' : '→';
                  const color =
                    t.delta > 0
                      ? 'text-green-700'
                      : t.delta < 0
                      ? 'text-red-700'
                      : 'text-slate-600';
                  return (
                    <div
                      key={topic}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="w-40 text-slate-700">
                        {TOPIC_LABELS[topic] ?? topic}
                      </span>
                      <span className="font-mono text-slate-500">
                        {t.firstAvg} → {t.lastAvg}
                      </span>
                      <span className={`w-20 text-right font-bold ${color}`}>
                        {arrow} {t.delta > 0 ? `+${t.delta}` : t.delta}
                      </span>
                    </div>
                  );
                })}
            </div>
          </Card>
        )}

        {/* Candidate progress */}
        {data.candidateProgress.length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Birden Fazla Sınava Giren Adaylar
            </h2>
            <p className="text-xs text-slate-500">
              Aynı aday her sınava katıldığında gelişimini takip ederiz
            </p>
            <ul className="mt-4 divide-y divide-slate-100">
              {data.candidateProgress.slice(0, 20).map((c) => {
                const arrow =
                  c.totalDelta > 0 ? '↑' : c.totalDelta < 0 ? '↓' : '→';
                const color =
                  c.totalDelta > 0
                    ? 'text-green-700'
                    : c.totalDelta < 0
                    ? 'text-red-700'
                    : 'text-slate-600';
                return (
                  <li
                    key={c.candidateId}
                    className="flex items-center justify-between py-3"
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {c.displayName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.attempts.length} sınav ·{' '}
                        {c.attempts.map((a) => a.score).join(' → ')}
                      </p>
                    </div>
                    <p className={`text-xl font-bold ${color}`}>
                      {arrow}{' '}
                      {c.totalDelta > 0 ? `+${c.totalDelta}` : c.totalDelta}
                    </p>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
